import { useMemo } from 'react';
import type { CompactPuzzle } from '@ricochet/game-engine';
import { buildWallSet, fromCompact } from '@ricochet/game-engine';
import {
  createBoardGeometry, computeWallRects, computeFrameBars,
  BoardDefs, BoardBackground, GridLayer, TargetsLayer, WallsLayer, RobotsLayer,
} from './BoardPrimitives.tsx';

const DEFAULT_PX = 128;
const PBORDER = 2;

interface BoardPreviewProps {
  puzzle: CompactPuzzle;
  size?: number;
  fluid?: boolean;
}

export default function BoardPreview({ puzzle: compact, size, fluid }: BoardPreviewProps) {
  const basePx = size ?? DEFAULT_PX;
  const puzzle = useMemo(() => fromCompact(compact), [compact]);
  const { gridSize, walls: wallSpecs, robots, targets } = puzzle;
  const cellSize = basePx / gridSize;

  const geo = useMemo(
    () => createBoardGeometry({ gridSize, cellSize, border: PBORDER, minWallThick: 1, wallOverhang: 0 }),
    [gridSize, cellSize],
  );

  const walls = useMemo(() => buildWallSet(wallSpecs, gridSize), [wallSpecs, gridSize]);
  const wallRects = useMemo(() => computeWallRects(walls, geo), [walls, geo]);
  const frameBars = useMemo(() => computeFrameBars(geo), [geo]);

  const svgSizeProps = fluid ? {} : { width: geo.totalSize, height: geo.totalSize };
  const svgStyle = fluid
    ? { display: 'block', borderRadius: 4, width: '100%', height: 'auto', overflow: 'visible' }
    : { display: 'block', borderRadius: 4, flexShrink: 0 as const, overflow: 'visible' };

  return (
    <svg
      {...svgSizeProps}
      viewBox={`0 0 ${geo.totalSize} ${geo.totalSize}`}
      style={svgStyle}
    >
      <BoardDefs geo={geo} idPrefix={`preview-${puzzle.id}`}
        gridStrokeWidth={0.5} gridOpacity={0.5} includeShadowFilter={false} />
      <BoardBackground geo={geo} outerRx={4} />
      <GridLayer geo={geo} patternId={`preview-${puzzle.id}-grid`} showMajorLines={false} />
      <TargetsLayer geo={geo} targets={targets} idPrefix={`preview-${puzzle.id}`}
        inset={0.5} strokeWidth={1}
        cornerRadius={0.12} fillOpacity={0.18} starYOffset={0.2} showStar={false} />
      <WallsLayer wallRects={wallRects} frameBars={frameBars} wallThick={geo.wallThick} />
      <RobotsLayer geo={geo} robots={robots} radiusFraction={0.34} showLabels={false} />
    </svg>
  );
}
